import type { PayRunApprovalDto, PayRunDetailDto } from './payRunsApiTypes';
import { approvalStreamLabel } from './payRunsModel';

/**
 * PRR-402 Approver presentation.
 *
 * The approver does not see the payroll user's screen with an extra panel: the design replaces the
 * processing timeline and the stat card row with a single lockup — a divided summary strip, one
 * horizontal approval chain and a comments row — and swaps the header actions.
 *
 * Why ONE chain when the backend approves two streams in parallel: a route carries the same
 * approvers in each stream, so the people form a single sequence even though EFT and Super are
 * decided independently. Grouping by approver reproduces the design and stays truthful, because an
 * approver only reads as Approved once they have approved every stream they sit on. When a route is
 * asymmetric the grouped step says so rather than hiding it — see `streamsDiverge`.
 *
 * Viewer identity: the UI auth store carries roles and a token but NO user id, so the frontend
 * cannot ask "is this person one of this run's approvers". The only trustworthy signal is the
 * server's per-requirement `canApprove` / `canReject` projection, which also gates the actions.
 */

export type ApproverChainStepState = 'done' | 'current' | 'upcoming' | 'rejected';

export interface ApproverChainStep {
  id: string;
  title: string;
  detail: string;
  state: ApproverChainStepState;
  /** Measured share of this approver's required streams that the server marks approved. */
  connectorPercent?: number;
}

export interface ApproverMetric {
  id: string;
  label: string;
  value: string;
  /** The design's hero figure, rendered larger than its neighbours. */
  hero?: boolean;
  /** True when the design specifies this metric but no API field supplies it. */
  unavailable?: boolean;
}

export interface ApproverComment {
  id: string;
  author: string;
  at: string | null;
  body: string;
}

export interface ApproverDecisionTarget {
  requirementId: string;
  streamLabel: string;
}

/** A component of the payment being approved, straight from an API total. */
export interface ApproverSplitSegment {
  id: 'net' | 'super';
  label: string;
  amount: number;
}

export interface PayRunApproverView {
  /** Requirements this viewer can act on right now, as projected by the server. */
  actionable: ApproverDecisionTarget[];
  decided: boolean;
  /** The route role this viewer is acting under, for the confirmation copy. */
  approvingAs: string | null;
  split: ApproverSplitSegment[];
  metrics: ApproverMetric[];
  /** One left-to-right chain, as the design draws it. */
  chain: ApproverChainStep[];
  /** True when an approver's decision differs between streams, so the chain says so. */
  streamsDiverge: boolean;
  status: { label: string; tone: 'blue' | 'positive' } | null;
  comments: ApproverComment[];
}

export interface PayRunApproverInput {
  payRun: PayRunDetailDto;
  approval: PayRunApprovalDto | null;
  employeeCount: number | null;
  /** True only for a completed approval in the current context and calculation. */
  decidedInSession?: boolean;
}

export interface RecordedApprovalDecision {
  payId: string;
  contextId: string;
  calculationHash: string;
  decision: 'approved';
  decidedRequirementIds: string[];
}

export const currentRecordedApproval = (
  recorded: RecordedApprovalDecision | null,
  payId: string,
  approval: PayRunApprovalDto | null,
): boolean => Boolean(recorded && approval && recorded.payId === payId
  && recorded.contextId === approval.contextId && recorded.calculationHash === approval.calculationHash
  && recorded.decision === 'approved' && !approval.isSuperseded
  && recorded.decidedRequirementIds.length > 0
  && recorded.decidedRequirementIds.every((id) => approval.streams.some((stream) =>
    stream.requirements.some((requirement) => requirement.requirementId === id
      && !requirement.isSuperseded && requirement.status === 'approved' && !requirement.canApprove))));

const money = (value: number | null | undefined): string =>
  typeof value === 'number'
    ? new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value)
    : '—';

const shortDate = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
  timeZone: 'Australia/Sydney',
});

const formatShort = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : shortDate.format(parsed);
};

const roleLabel = (role: string): string =>
  role === 'payroll_manager' ? 'Payroll manager' : role === 'finance_delegate' ? 'Finance delegate' : role;

const liveRequirements = (approval: PayRunApprovalDto | null) =>
  (approval?.streams ?? []).flatMap((stream) =>
    stream.requirements
      .filter((requirement) => !requirement.isSuperseded)
      .map((requirement) => ({ ...requirement, stream: stream.approvalStream })));

export const isApproverView = ({ approval, decidedInSession = false }: PayRunApproverInput): boolean => {
  if (decidedInSession) return true;
  return liveRequirements(approval).some((requirement) => requirement.canApprove || requirement.canReject);
};

export const buildPayRunApproverView = (input: PayRunApproverInput): PayRunApproverView => {
  const { payRun, approval, employeeCount, decidedInSession = false } = input;
  const calculated = payRun.calculation?.status === 'complete';
  const requirements = liveRequirements(approval);

  // Group by approver so the chain reads as a sequence of people, as the design does.
  const byApprover = new Map<string, typeof requirements>();
  for (const requirement of requirements) {
    const existing = byApprover.get(requirement.approverUserId);
    if (existing) existing.push(requirement);
    else byApprover.set(requirement.approverUserId, [requirement]);
  }

  const approvers = [...byApprover.entries()]
    .map(([approverUserId, group]) => ({
      approverUserId,
      group,
      order: Math.min(...group.map((requirement) => requirement.approvalOrder)),
    }))
    .sort((left, right) => left.order - right.order);

  const actionable: ApproverDecisionTarget[] = [];
  const comments: ApproverComment[] = [];
  let approvingAs: string | null = null;
  let streamsDiverge = false;

  for (const requirement of requirements) {
    if (requirement.canApprove || requirement.canReject) {
      actionable.push({ requirementId: requirement.requirementId, streamLabel: approvalStreamLabel(requirement.stream) });
      approvingAs ??= roleLabel(requirement.approverRole);
    }
    if (requirement.comment) {
      comments.push({
        id: `${requirement.requirementId}-comment`,
        author: requirement.decidedBy ?? requirement.approverUserId,
        at: requirement.decidedAt ?? null,
        body: requirement.comment,
      });
    }
  }

  const submittedAt = formatShort(approval?.submittedAt);
  const chain: ApproverChainStep[] = [{
    id: 'submitted',
    title: approval?.submittedAt ? `Submitted${submittedAt ? ` ${submittedAt}` : ''}` : 'Not submitted',
    detail: approval?.submittedBy ?? 'Payroll',
    state: approval?.submittedAt ? 'done' : 'upcoming',
  }];

  let reachedCurrent = false;
  approvers.forEach((approver, index) => {
    const statuses = new Set(approver.group.map((requirement) => requirement.status));
    streamsDiverge ||= statuses.size > 1;

    const rejected = statuses.has('rejected');
    // Approved only once every stream this person sits on is approved — a half-approved
    // approver must not read as done.
    const approved = !rejected && approver.group.every((requirement) => requirement.status === 'approved');
    const current = !approved && !rejected && !reachedCurrent && Boolean(approval?.submittedAt);
    if (current) reachedCurrent = true;
    // Being next in the route is not the same as being the viewer. Only the server says whether
    // this viewer can act, so second-person wording is gated on that and nothing else.
    const mine = current && approver.group.some((requirement) => requirement.canApprove || requirement.canReject);

    const decidedAt = formatShort(
      approver.group.map((requirement) => requirement.decidedAt).filter(Boolean).sort().at(-1) ?? null,
    );
    const isLast = index === approvers.length - 1;
    const outstanding = approver.group
      .filter((requirement) => requirement.status !== 'approved')
      .map((requirement) => approvalStreamLabel(requirement.stream));

    chain.push({
      id: approver.approverUserId,
      title: rejected
        ? 'Changes requested'
        : approved
          ? `Approved${decidedAt ? ` ${decidedAt}` : ''}`
          : mine ? 'You’re next' : isLast ? 'Final approval' : 'Approval',
      detail: streamsDiverge && outstanding.length > 0 && outstanding.length < approver.group.length
        ? `${roleLabel(approver.group[0].approverRole)} · awaiting ${outstanding.join(', ')}`
        : mine
          ? 'Pending your approval'
          : roleLabel(approver.group[0].approverRole),
      state: rejected ? 'rejected' : approved ? 'done' : current ? 'current' : 'upcoming',
      connectorPercent: Math.round((approver.group.filter((requirement) => requirement.status === 'approved').length
        / approver.group.length) * 100),
    });
  });

  const released = payRun.workflowStatus === 'complete';
  chain.push({
    id: 'released',
    title: 'Pay released',
    detail: released ? 'Released' : 'Not started',
    state: released ? 'done' : 'upcoming',
  });

  // The design's hero "To approve" and "Deductions" are not API fields. Summing Net and Super in the
  // browser would be deriving a payroll figure client-side, which this screen must never do.
  const metrics: ApproverMetric[] = [
    { id: 'to_approve', label: 'To approve', value: '—', hero: true, unavailable: true },
    { id: 'gross', label: 'Gross pay', value: calculated ? money(payRun.gross) : '—' },
    { id: 'tax', label: 'Tax withheld', value: calculated ? money(payRun.tax) : '—' },
    {
      id: 'net',
      label: employeeCount === null
        ? 'Net pay'
        : `Net pay - ${employeeCount} ${employeeCount === 1 ? 'employee' : 'employees'}`,
      value: calculated ? money(payRun.netPay) : '—',
    },
    { id: 'super', label: 'Super', value: calculated ? money(payRun.super) : '—' },
    { id: 'deductions', label: 'Deductions', value: '—', unavailable: true },
  ];

  const split: ApproverSplitSegment[] = calculated
    ? [
      {
        id: 'net' as const,
        label: employeeCount === null
          ? 'Net pay'
          : `Net pay to ${employeeCount} ${employeeCount === 1 ? 'employee' : 'employees'}`,
        amount: payRun.netPay,
      },
      { id: 'super' as const, label: 'Super', amount: payRun.super },
    ].filter((segment) => segment.amount > 0)
    : [];

  const decided = decidedInSession && actionable.length === 0;
  const approverSteps = chain.filter((step) => step.id !== 'submitted' && step.id !== 'released');
  const hasRejection = approverSteps.some((step) => step.state === 'rejected');
  const allApproved = approverSteps.length > 0 && approverSteps.every((step) => step.state === 'done');

  return {
    actionable,
    decided,
    approvingAs,
    split,
    metrics,
    chain,
    streamsDiverge,
    status: approval?.submittedAt
      ? (hasRejection
        ? { label: 'Changes requested', tone: 'blue' as const }
        : allApproved
          ? { label: 'Approved', tone: 'positive' as const }
          : { label: 'Pending', tone: 'blue' as const })
      : null,
    comments,
  };
};
